import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "chart.js/auto";
import QueryBlock from "../../components/QueryBlock";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { useSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";
import { getSensorEventLog } from "../../apis/SensorEvent.Api";

const EventBarChart = ({ selectedBuilding }) => {
    const rooms = useSelector((state) => state.room.data);

    const { enqueueSnackbar } = useSnackbar();

    const [queryStartDate, setQueryStartDate] = useState("");
    const [queryEndDate, setQueryEndDate] = useState("");
    const [queryGender, setQueryGender] = useState("ALL");
    const [queryRooms, setQueryRooms] = useState([]);
    const [querySensors, setQuerySensors] = useState(["ALL"]);
    const [statistics, setStatistics] = useState({
        total: 0,
        average: 0,
        max: 0,
        min: 0,
    });
    const [labels, setLabels] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    async function handleSubmit() {
        setIsLoading(true);

        let realRooms =
            queryRooms.length > 0
                ? [...queryRooms]
                : Object.values(rooms)
                      .filter((room) => room.buildingId === selectedBuilding)
                      .map((room) => room.roomId);

        realRooms.sort((a, b) => {
            const aRawFloor = a.split("-")[1];
            const bRawFloor = b.split("-")[1];

            let aFloor = aRawFloor?.replace("B", "-") || "";
            let bFloor = bRawFloor?.replace("B", "-") || "";

            if (aFloor[aFloor.length - 1] === "F") {
                aFloor = aFloor.substring(0, aFloor.length - 1);
            }

            if (bFloor[bFloor.length - 1] === "F") {
                bFloor = bFloor.substring(0, bFloor.length - 1);
            }

            if (Number(bFloor) === Number(aFloor)) {
                const aRawGender = a.split("-")[2];

                if (aRawGender === "M") {
                    return 1;
                } else {
                    return -1;
                }
            }

            return aRawFloor?.startsWith("B") && bRawFloor?.startsWith("B")
                ? Number(bFloor) - Number(aFloor)
                : Number(aFloor) - Number(bFloor);
        });

        realRooms =
            queryGender === "ALL"
                ? [...realRooms]
                : [...realRooms].filter((roomId) => {
                      const splitArr = roomId.split("-");
                      return splitArr[2]?.startsWith(queryGender[0]);
                  });

        try {
            const res = await getSensorEventLog({
                room_id: JSON.stringify(realRooms),
                start_time: moment(moment(queryStartDate, "YYYYMMDD").format("YYYY/MM/DD") + " 00:00:00.000").format(
                    "YYMMDDHHmmssSSS"
                ),
                end_time: moment(moment(queryEndDate, "YYYYMMDD").format("YYYY/MM/DD") + " 23:59:59.999").format(
                    "YYMMDDHHmmssSSS"
                ),
            });
            if (!res.data.indicator) {
                enqueueSnackbar(res.data.message, { variant: "error" });
                setIsLoading(false);
                return;
            }

            const raw_ds = {
                AMMONIA: {},
                SOAP: {},
                ["HAND TOWEL"]: {},
                ["PAPER ROLL"]: {},
            };

            realRooms.forEach((roomId) => {
                raw_ds["AMMONIA"][roomId] = 0;
                raw_ds["SOAP"][roomId] = 0;
                raw_ds["HAND TOWEL"][roomId] = 0;
                raw_ds["PAPER ROLL"][roomId] = 0;
            });

            res.data.message.forEach((event) => {
                raw_ds[event.sensor_type.toUpperCase()][event.room_id] += 1;
            });

            setLabels(realRooms);

            const dataset = ["AMMONIA", "SOAP", "HAND TOWEL", "PAPER ROLL"]
                .filter((sensorType) => querySensors.includes(sensorType))
                .map((sensorType) => ({
                    label: sensorType,
                    data: realRooms.map((roomId) => raw_ds[sensorType][roomId]),
                }));

            setDatasets(dataset);

            const combineData = realRooms.map((_, index) => {
                return dataset.reduce((acc, curr) => {
                    return acc + curr.data[index];
                }, 0);
            });
            const total = combineData.reduce((acc, data) => {
                return acc + data;
            }, 0);

            setStatistics({
                total: total,
                average: (total / realRooms.length).toFixed(2),
                max: Math.max(...combineData),
                min: Math.min(...combineData),
            });
        } catch (error) {
            console.log(error);
            enqueueSnackbar(error.data.message, { variant: "error" });
        }

        setIsLoading(false);
    }

    useEffect(() => {
        setLabels([]);
        setDatasets([]);
        setStatistics({
            total: 0,
            average: 0,
            max: 0,
            min: 0,
        });
    }, [selectedBuilding]);
    return (
        <div className="footfall-line-chart">
            <QueryBlock
                selectedBuilding={selectedBuilding}
                enableQueryHour={false}
                queryStartDate={queryStartDate}
                setQueryStartDate={setQueryStartDate}
                queryEndDate={queryEndDate}
                setQueryEndDate={setQueryEndDate}
                queryGender={queryGender}
                setQueryGender={setQueryGender}
                queryRooms={queryRooms}
                setQueryRooms={setQueryRooms}
                querySensors={querySensors}
                setQuerySensors={setQuerySensors}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
            />
            <div className="chart-graph">
                <div className="chart-graph-content">
                    {isLoading ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <Bar
                            data={{
                                labels: labels,
                                datasets: datasets,
                            }}
                            options={{
                                animation: {
                                    duration: 1000,
                                },
                                indexAxis: "y",
                                scales: {
                                    x: {
                                        stacked: true,
                                        beginAtZero: true,
                                    },
                                    y: {
                                        stacked: true,
                                        beginAtZero: true,
                                    },
                                },
                                plugins: {
                                    annotation:
                                        statistics.average > 0
                                            ? {
                                                  annotations: {
                                                      verticalLine: {
                                                          type: "line",
                                                          xMin: statistics.average,
                                                          xMax: statistics.average,
                                                          borderColor: "gray",
                                                          borderWidth: 2,
                                                          borderDash: [5, 5],
                                                          label: {
                                                              content: "Average",
                                                              enabled: true,
                                                              position: "bottom",
                                                          },
                                                      },
                                                  },
                                              }
                                            : {},
                                },
                            }}
                        />
                    )}
                </div>
                <div className="chart-graph-statistics">
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Total</div>
                        <div className="chart-graph-statistics-block-number">{statistics.total.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Average</div>
                        <div className="chart-graph-statistics-block-number">{statistics.average.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Max</div>
                        <div className="chart-graph-statistics-block-number">{statistics.max.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Min</div>
                        <div className="chart-graph-statistics-block-number">{statistics.min.toLocaleString()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventBarChart;
